import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  drawerAccordionSx,
  drawerAccordionSummarySx,
  outlinedFieldSx,
  sectionChipSx,
  muestraDatePickerPortalProps,
} from "../drawerSurfaceSx";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DatePicker from "react-datepicker";
import { es } from "date-fns/locale";
import {
  applyMuestraFromMovimientos,
  blocksNewShipmentUntilReception,
} from "../muestraEstadoHelpers";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { formatMuestraDateForAPI } from "../../../../utils/dateUtils";
import EnvioDestinoFields, {
  emptyEnvioDestino,
  DESTINO_CLIENTE,
  DESTINO_PROVEEDOR,
  DESTINO_INTERNO,
} from "../../EnvioDestinoFields";

export default function MovimientosSection({
  movimientos,
  setMovimientos,
  tiposMarcas,
  muestra,
  setMuestra,
  deleteMovimiento,
  sentToClientEstadoId,
  sentToSupplierEstadoId,
  tiposEstado = [],
  canCourierSolicitar = false,
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const accSx = drawerAccordionSx(theme);
  const sumBase = drawerAccordionSummarySx(theme);
  const fin = outlinedFieldSx(theme);
  const chipSx = sectionChipSx(theme);

  const [openDialog, setOpenDialog] = useState(false);
  const [pendingIndex, setPendingIndex] = useState(null);

  const [expanded, setExpanded] = useState(() => movimientos.length > 0);

  const [openLastShipmentDialog, setOpenLastShipmentDialog] = useState(false);

  const blockNewShipment = blocksNewShipmentUntilReception(
    muestra?.ID_ESTADO,
    tiposEstado,
    muestra?.ESTADO_NOMBRE || muestra?.ESTADO || null,
  );

  // Si ya está bloqueado, quitar borradores sin guardar para que no se puedan completar.
  useEffect(() => {
    if (!blockNewShipment) return;
    setMovimientos((prev) => {
      const next = prev.filter((m) => m.ID_MOVIMIENTO);
      return next.length === prev.length ? prev : next;
    });
  }, [blockNewShipment, setMovimientos]);

  const addMov = () => {
    if (!muestra.ID_MUESTRA) {
      alert(t("Samples.saveSampleFirst"));
      return;
    }
    if (!canCourierSolicitar) {
      alert(t("Samples.courierSolicitarRequired"));
      return;
    }
    if (blockNewShipment) {
      alert(t("Samples.shipmentPendingReceptionBody"));
      return;
    }

    setMovimientos((prev) => [
      ...prev,
      {
        ...emptyEnvioDestino(),
        FECHA_ENVIO: null,
        NUM_COURIER: "",
      },
    ]);
  };

  const askDelete = (index) => {
    const mov = movimientos[index];

    const movimientosConFecha = movimientos.filter((m) => m.FECHA_ENVIO);

    const isDeletingLastShipment =
      mov.FECHA_ENVIO && movimientosConFecha.length === 1;

    if (isDeletingLastShipment) {
      setPendingIndex(index);
      setOpenLastShipmentDialog(true);
      return;
    }

    setPendingIndex(index);
    setOpenDialog(true);
  };

  const confirmDelete = async () => {
    const i = pendingIndex;
    if (i === null) return;

    const mov = movimientos[i];

    if (mov.ID_MOVIMIENTO) {
      try {
        await deleteMovimiento(mov.ID_MOVIMIENTO);
      } catch (err) {
        console.error("Error deleting movement:", err);
        alert(t("Samples.deleteMovementError"));
        return;
      }
    }

    setMovimientos((prev) => {
      const updated = prev.filter((_, idx) => idx !== i);

      const fechasValidas = updated.map((m) => m.FECHA_ENVIO).filter(Boolean);

      if (fechasValidas.length === 0) {
        setMuestra((prevMuestra) => ({
          ...prevMuestra,
          FECHA_ENVIO: null,
          ID_ESTADO: 3,
        }));
      } else {
        applyMuestraFromMovimientos({
          movimientos: updated,
          tiposMarcas,
          sentToClientEstadoId,
          sentToSupplierEstadoId,
          setMuestra,
        });
      }

      return updated;
    });

    setPendingIndex(null);
    setOpenDialog(false);
  };

  const cancelDelete = () => {
    setPendingIndex(null);
    setOpenDialog(false);
  };

  const update = (i, field, val) => {
    setMovimientos((prev) => {
      const updated = prev.map((m, idx) =>
        idx === i ? { ...m, [field]: val } : m,
      );

      if (
        field === "FECHA_ENVIO" ||
        field === "ID_Marca" ||
        field === "TIPO_DESTINO" ||
        field === "ID_CLIENTE" ||
        field === "CODPROV"
      ) {
        applyMuestraFromMovimientos({
          movimientos: updated,
          tiposMarcas,
          sentToClientEstadoId,
          sentToSupplierEstadoId,
          setMuestra,
        });
      }

      return updated;
    });
  };

  const updateDestino = (i, nextDestino) => {
    setMovimientos((prev) => {
      const updated = prev.map((m, idx) =>
        idx === i
          ? {
              ...m,
              TIPO_DESTINO: nextDestino.TIPO_DESTINO || null,
              ID_CLIENTE: nextDestino.ID_CLIENTE ?? null,
              CODPROV: nextDestino.CODPROV ?? null,
              ID_CONTACTO: nextDestino.ID_CONTACTO ?? null,
              PERSONA_ENVIO: nextDestino.PERSONA_ENVIO || "",
              ID_Marca: nextDestino.TIPO_DESTINO
                ? null
                : (nextDestino.ID_Marca ?? m.ID_Marca ?? null),
            }
          : m,
      );

      applyMuestraFromMovimientos({
        movimientos: updated,
        tiposMarcas,
        sentToClientEstadoId,
        sentToSupplierEstadoId,
        setMuestra,
      });

      return updated;
    });
  };

  const getLegacyMarcaName = (mov) => {
    if (!mov?.ID_Marca) return "";
    return (
      tiposMarcas.find((m) => Number(m.ID_MARCA) === Number(mov.ID_Marca))
        ?.MARCA || ""
    );
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        sx={accSx}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            ...sumBase,
            "& .MuiAccordionSummary-content": {
              ...sumBase["& .MuiAccordionSummary-content"],
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", mr: 0.5 }}>
            {t("Samples.shipments")}
          </Typography>

          <Chip
            size="small"
            label={String(movimientos.length)}
            sx={{ ...chipSx, mr: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          />

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              addMov();
            }}
            sx={{ ml: 0.5 }}
            color="primary"
            disabled={blockNewShipment || !canCourierSolicitar}
          >
            <AddCircleOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 1.25, pb: 1.25 }}>
          {!canCourierSolicitar ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.25 }}
            >
              {t("Samples.courierSolicitarRequired")}
            </Typography>
          ) : null}
          {blockNewShipment ? (
            <Typography variant="body2" color="warning.main" sx={{ mb: 1.25 }}>
              {t("Samples.shipmentPendingReceptionBody")}
            </Typography>
          ) : null}
          {movimientos.map((mov, i) => (
            <Box
              key={mov.ID_MOVIMIENTO || `new-${i}`}
              mb={1.5}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1.25,
              }}
            >
              <Box
                display="flex"
                gap={1.25}
                alignItems="flex-start"
                sx={{ flexWrap: "wrap", minWidth: 0 }}
              >
                <Box sx={{ flex: "1 1 100%", minWidth: 0 }}>
                  <EnvioDestinoFields
                    value={{
                      TIPO_DESTINO: mov.TIPO_DESTINO || "",
                      ID_CLIENTE: mov.ID_CLIENTE ?? null,
                      CODPROV: mov.CODPROV ?? null,
                      ID_CONTACTO: mov.ID_CONTACTO ?? null,
                      PERSONA_ENVIO: mov.PERSONA_ENVIO || "",
                      ID_Marca: mov.ID_Marca ?? null,
                    }}
                    onChange={(next) => updateDestino(i, next)}
                    compact
                    disabled={blockNewShipment}
                    fieldSx={fin}
                    allowedTipos={[
                      DESTINO_CLIENTE,
                      DESTINO_PROVEEDOR,
                      DESTINO_INTERNO,
                    ]}
                    legacyMarcaName={getLegacyMarcaName(mov)}
                  />
                </Box>

                <Box sx={{ flex: "0 0 132px", minWidth: 132 }}>
                  <DatePicker
                    selected={
                      mov.FECHA_ENVIO ? new Date(mov.FECHA_ENVIO) : null
                    }
                    locale={es}
                    dateFormat="dd/MM/yyyy"
                    disabled={blockNewShipment}
                    {...muestraDatePickerPortalProps}
                    onChange={(date) => {
                      if (blockNewShipment) return;
                      if (!date) {
                        update(i, "FECHA_ENVIO", null);
                        return;
                      }

                      const safe = new Date(date);
                      safe.setHours(12, 0, 0, 0);

                      update(i, "FECHA_ENVIO", formatMuestraDateForAPI(safe));
                    }}
                    customInput={
                      <TextField
                        size="small"
                        fullWidth
                        label={t("Samples.sendDate")}
                        sx={fin}
                        disabled={blockNewShipment}
                      />
                    }
                  />
                </Box>

                <TextField
                  size="small"
                  label={t("Samples.courierNumber")}
                  value={mov.NUM_COURIER || ""}
                  onChange={(e) => update(i, "NUM_COURIER", e.target.value)}
                  disabled={blockNewShipment}
                  sx={{ ...fin, flex: "1 1 140px", minWidth: 120 }}
                />

                {mov.IdCourierControl ? (
                  <Chip
                    component={RouterLink}
                    to={`/courier-control?id=${mov.IdCourierControl}`}
                    clickable
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={t("courierControl.openShipment")}
                    sx={{ mt: 0.5 }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : null}

                <IconButton
                  color="error"
                  size="small"
                  onClick={() => askDelete(i)}
                  sx={{
                    flexShrink: 0,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    mt: 0.25,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <DeleteOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      <Dialog open={openDialog} onClose={cancelDelete}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t("Samples.deleteMovementTitle")}
        </DialogTitle>

        <DialogContent>
          <Typography>{t("Samples.deleteMovementBody")}</Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={cancelDelete} variant="outlined">
            {t("Samples.cancel")}
          </Button>

          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            sx={{ color: "white" }}
          >
            {t("Samples.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openLastShipmentDialog}
        onClose={() => setOpenLastShipmentDialog(false)}
      >
        <DialogTitle>{t("Samples.revertShipmentTitle")}</DialogTitle>

        <DialogContent>
          <Typography>{t("Samples.revertShipmentBody")}</Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenLastShipmentDialog(false)}
            variant="outlined"
          >
            {t("Samples.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            sx={{ color: "white" }}
            onClick={async () => {
              setOpenLastShipmentDialog(false);
              await confirmDelete();
            }}
          >
            {t("Samples.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
